// =====================================================================================
// INVRT Radar — web console — account — Usage tab
//
// Where the credits went, grouped by action. Moved verbatim out of AccountPage.tsx in
// the C1 split; the page shell registers it under the tab id "usage".
//
// Every figure here is one Postgres computed and the API handed over — the window total
// comes from the response's own `totals` block, never from re-adding the rows on screen.
// =====================================================================================

import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { formatCredits, formatDateTime, humaniseAction } from "@/lib/format";
import type { UsageSummaryResponse } from "@/lib/types";

export function UsagePanel() {
  const api = useApiClient();
  const { orgId } = useSession();

  const usage = useQuery({
    queryKey: ["usage-summary", orgId],
    queryFn: () => api.get<UsageSummaryResponse>("/ledger/usage-summary"),
    enabled: Boolean(orgId),
  });

  if (usage.isLoading) return <LoadingState label="Loading your usage…" />;
  if (usage.isError) return <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />;
  if (!usage.data) return null;

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>Where the credits went</CardTitle>
          <p className="mt-1 text-sm text-ink-muted">
            {formatDateTime(usage.data.window.from)} to {formatDateTime(usage.data.window.to)}
          </p>
        </div>
        <Badge tone="info">
          Net {formatCredits(usage.data.totals.window_credits_delta)}
        </Badge>
      </CardHeader>
      <CardContent>
        {usage.data.by_action.length === 0 ? (
          <EmptyState
            title="Nothing used in this period"
            body="Once you reveal a contact or identify a visitor, the totals appear here grouped by what you did."
          />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Action</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell className="text-right">Times</TableHeaderCell>
                <TableHeaderCell className="text-right">Units</TableHeaderCell>
                <TableHeaderCell className="text-right">Credits</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usage.data.by_action.map((row) => (
                <TableRow key={`${row.action ?? "none"}-${row.entry_type}`}>
                  <TableCell className="font-medium">{humaniseAction(row.action)}</TableCell>
                  <TableCell className="text-ink-muted">
                    {humaniseAction(row.entry_type)}
                  </TableCell>
                  <TableCell className="text-right">
                    {row.entry_count.toLocaleString("en-GB")}
                  </TableCell>
                  <TableCell className="text-right text-ink-muted">
                    {formatCredits(row.unit_count_total)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCredits(row.credits_delta_total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
